// components/DateSelector.js
import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../api/apiClient';
import '../styles/DateSelector.css';

function DateSelector({ selectedDate, onDateChange, deviceId, serialNumber }) {
  
  const getFranceToday = () => {
    return new Date().toLocaleDateString('en-CA', {
      timeZone: 'Europe/Paris'
    });
  };
  
  const [availableDates, setAvailableDates] = useState([]);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(getFranceToday());
  const [isLoading, setIsLoading] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date(selectedDate));
  const [highlightedDates, setHighlightedDates] = useState(new Set());
  const calendarRef = useRef(null);

  // Fetch available dates when component mounts or device changes
  useEffect(() => {
    fetchAvailableDates();
  }, [deviceId, serialNumber]);

  // Close calendar when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchAvailableDates = async () => {
    if (!deviceId && !serialNumber) return;
    
    setIsLoading(true);
    try {
      const identifier = deviceId || serialNumber;
      const response = await apiFetch(
        `/api/monitoring/available-dates?device=${identifier}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAvailableDates(data.dates || []);
        
        if (data.dates.length > 0) {
          setMinDate(data.dates[0]); // Oldest date
          setMaxDate(data.dates[data.dates.length - 1]); // Newest date
          setHighlightedDates(new Set(data.dates));
        }
      }
    } catch (error) {
      console.error('Error fetching available dates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateClick = (date) => {
    const dateStr = formatLocalDate(date);
    if (highlightedDates.has(dateStr)) {
      onDateChange(dateStr);
      setShowCalendar(false);
    }
  };

  const handleMonthChange = (increment) => {
    const newMonth = new Date(calendarMonth);
    newMonth.setMonth(newMonth.getMonth() + increment);
    setCalendarMonth(newMonth);
  };

  const renderCalendar = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay()); // Start from Sunday
    
    const weeks = [];
    let days = [];
    let currentDate = new Date(startDate);

    // Generate 6 weeks (42 days) to ensure full calendar
    for (let i = 0; i < 42; i++) {
      if (days.length === 7) {
        weeks.push(days);
        days = [];
      }
      
      const dateStr = formatLocalDate(currentDate);
      const isCurrentMonth = currentDate.getMonth() === month;
      const hasData = highlightedDates.has(dateStr);
      const isToday = dateStr === formatLocalDate(new Date());
      const isSelected = dateStr === selectedDate;
      const isDisabled = !hasData || currentDate > new Date();
      
      days.push({
        date: new Date(currentDate),
        dateStr,
        isCurrentMonth,
        hasData,
        isToday,
        isSelected,
        isDisabled
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    weeks.push(days);

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];

    return (
      <div className="custom-calendar">
        <div className="calendar-header">
          <button 
            onClick={() => handleMonthChange(-1)}
            className="calendar-nav-btn"
            disabled={minDate && new Date(year, month) <= new Date(minDate)}
          >
            ‹
          </button>
          <span className="calendar-month-year">
            {monthNames[month]} {year}
          </span>
          <button 
            onClick={() => handleMonthChange(1)}
            className="calendar-nav-btn"
            disabled={maxDate && new Date(year, month + 1) > new Date(maxDate)}
          >
            ›
          </button>
        </div>
        
        <div className="calendar-weekdays">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>
        
        <div className="calendar-grid">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="calendar-week">
              {week.map((day, dayIndex) => (
                <button
                  key={dayIndex}
                  className={`calendar-day 
                    ${!day.isCurrentMonth ? 'other-month' : ''}
                    ${day.hasData ? 'has-data' : ''}
                    ${day.isToday ? 'today' : ''}
                    ${day.isSelected ? 'selected' : ''}
                    ${day.isDisabled ? 'disabled' : ''}
                  `}
                  onClick={() => handleDateClick(day.date)}
                  disabled={day.isDisabled}
                >
                  {day.date.getDate()}
                  {day.hasData && <span className="data-dot"></span>}
                </button>
              ))}
            </div>
          ))}
        </div>
        
        <div className="calendar-footer">
          <div className="calendar-legend">
            <span className="legend-item">
              <span className="data-dot"></span> Data Available
            </span>
            <span className="legend-item">
              <span className="selected-dot"></span> Selected
            </span>
          </div>
          {minDate && (
            <div className="date-range">
              {new Date(minDate).toLocaleDateString('en-US', { 
                day: 'numeric', month: 'short', year: 'numeric' 
              })} - {new Date(maxDate).toLocaleDateString('en-US', { 
                day: 'numeric', month: 'short', year: 'numeric' 
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="date-selector-container" ref={calendarRef}>
      <div className="date-input-wrapper">
        <div 
          className={`date-display ${isLoading ? 'loading' : ''}`}
          onClick={() => setShowCalendar(!showCalendar)}
        >
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="#D7D7CC" strokeWidth="1.4">
            <rect x="1.5" y="3" width="13" height="11.5"></rect>
            <path d="M1.5 6.5h13M5 1.5v3M11 1.5v3"></path>
          </svg>
          <span>
            {new Date(selectedDate).toLocaleDateString('en-US', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
              year: 'numeric'
            }).replace(',', '').toUpperCase()}
          </span>
          <span className="date-caret">{showCalendar ? '▲' : '▼'}</span>
        </div>
        
        {isLoading && (
          <div className="date-loading">
            <div className="spinner-small"></div>
          </div>
        )}
      </div>

      {showCalendar && (
        <>
          {/* Backdrop */}
          <div 
            className="calendar-backdrop"
            onClick={() => setShowCalendar(false)}
          />

          {/* Dropdown */}
          <div className="calendar-dropdown">
            {renderCalendar()}
          </div>
        </>
      )}
    </div>
  );
}

export default DateSelector;
